import React, { useRef, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTeamPresence } from "../../contexts/TeamPresenceContext";
import { useLayout } from "../../contexts/LayoutContext";
import useDark from "../../Center/Graph/hooks/useDark";

import entitylogo from "../../../assets/images/entitylogo.png";

const cyan = "14,165,233";
const cyanLit = "56,189,248";

const TreeSection = () => {
  const { currentAppPage, sections, leftOpen } = useLayout();
  const dark = useDark();
  const [expandedSections, setExpandedSections] = useState({});
  const [wikiCounts, setWikiCounts] = useState(null);
  const { unreadCounts } = useTeamPresence();
  const totalUnread = Object.values(unreadCounts).reduce(
    (sum, n) => sum + n,
    0,
  );
  const activeRef = useRef(null);

  useEffect(() => {
    if (currentAppPage && leftOpen) {
      setExpandedSections((prev) => ({ ...prev, [currentAppPage]: true }));
    }
  }, [currentAppPage, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      const path = e.detail?.path;
      if (!path) return;
      setExpandedSections({ [path]: true });
      setTimeout(() => {
        activeRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 250);
    };
    window.addEventListener("search:navigate", handler);
    return () => window.removeEventListener("search:navigate", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setWikiCounts(e.detail);
    };
    window.addEventListener("sidebar:wiki-counts", handler);
    return () => window.removeEventListener("sidebar:wiki-counts", handler);
  }, []);

  const toggleExpandState = (path) => {
    setExpandedSections((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const toggleExpand = (path, e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleExpandState(path);
  };

  const portalSlotId = (path) => `${path.replace(/^\//, "")}-portal-slot`;

  // Active border: cyan in dark, slate in light — matches bottom bar / panels exactly
  const activeBorder = dark ? `rgba(${cyan},0.38)` : `rgba(100,116,139,0.28)`;

  // Active bg: very faint radial tint from the top (dark only)
  const activeInnerGlow = dark
    ? `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${cyan},0.06) 0%, transparent 100%)`
    : "none";

  const rowBase = [
    "relative group w-full flex items-center rounded-lg",
    "px-2.5 py-1.5",
    "transition-all duration-200 ease-out",
    "outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
  ].join(" ");

  const inactiveRowClasses = [rowBase, "hover:bg-mainButtonBg/50"].join(" ");
  const activeRowClasses = rowBase;

  const iconClasses = (isActive) =>
    [
      "h-[18px] w-[18px] shrink-0 transition-colors duration-200",
      isActive ? "text-velBlue" : "text-mutedText group-hover:text-mainText",
    ].join(" ");

  const labelClasses = (isActive) =>
    [
      "truncate text-[13px] font-normal tracking-wide transition-colors duration-200",
      isActive ? "text-velBlue" : "text-mutedText group-hover:text-mainText",
    ].join(" ");

  const renderIcon = (el, isActive) => {
    if (React.isValidElement(el)) {
      return React.cloneElement(el, {
        className: [el.props.className || "", iconClasses(isActive)]
          .join(" ")
          .trim(),
      });
    }
    return <span className={iconClasses(isActive)}>{el}</span>;
  };

  const chevronClasses = (isExpanded) =>
    [
      "h-3.5 w-3.5 shrink-0 transition-all duration-200",
      "text-mutedText group-hover:text-mainText",
      isExpanded ? "opacity-100" : "opacity-0 group-hover:opacity-100",
    ]
      .join(" ")
      .trim();

  return (
    <>
      <style>{`
        @keyframes arwes-nav-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.06),
              0 0 12px 2px rgba(${cyan},0.08),
              0 0 28px 3px rgba(${cyan},0.03);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.12),
              0 0 16px 3px rgba(${cyan},0.13),
              0 0 40px 5px rgba(${cyan},0.05);
          }
        }
      `}</style>

      <nav className="flex flex-col w-full gap-y-0.5 px-2.5">
        {sections.map((section) => {
          const isActiveSection = currentAppPage === section.path;
          const isExpanded = !!expandedSections[section.path];
          const hasSubItems = !!section.hasSubItems;

          return (
            <div
              key={section.path || section.label}
              className="rounded-lg transition-all duration-200 ease-out overflow-hidden"
              style={
                isActiveSection
                  ? {
                      border: `1px solid ${activeBorder}`,
                      background:
                        activeInnerGlow !== "none"
                          ? activeInnerGlow
                          : undefined,
                      animation: "arwes-nav-pulse 5s ease-in-out infinite",
                    }
                  : {
                      border: "1px solid transparent",
                      background: "transparent",
                    }
              }
            >
              <Link
                ref={isActiveSection ? activeRef : null}
                to={section.path}
                aria-label={section.label}
                aria-current={isActiveSection ? "page" : undefined}
                className={
                  isActiveSection ? activeRowClasses : inactiveRowClasses
                }
                onClick={(e) => {
                  if (hasSubItems) {
                    e.preventDefault();
                    toggleExpand(section.path, e);
                  }
                }}
              >
                <div className="relative z-10 flex items-center justify-between gap-x-2.5 min-h-[28px] w-full">
                  <div className="flex items-center gap-x-2.5 min-w-0">
                    {renderIcon(section.icon, isActiveSection)}
                    <span className={labelClasses(isActiveSection)}>
                      {section.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-x-1">
                    {section.path === "/wiki" && wikiCounts && isExpanded && (
                      <span className="text-[11px] tabular-nums tracking-tight shrink-0">
                        <span className="text-activeCase">
                          {wikiCounts.active}
                        </span>
                        <span className="text-mutedText">
                          /{wikiCounts.maxActive}
                        </span>
                      </span>
                    )}

                    {section.path === "/chat" &&
                      totalUnread > 0 &&
                      !isExpanded && (
                        <span className="bg-velBlue text-copyText text-[10px] font-normal rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shrink-0">
                          {totalUnread > 99 ? "99+" : totalUnread}
                        </span>
                      )}

                    {hasSubItems && (
                      <button
                        type="button"
                        onClick={(e) => toggleExpand(section.path, e)}
                        className="p-0.5 rounded-lg hover:bg-mainButtonBg/50 transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <ChevronUp
                            className={chevronClasses(isExpanded)}
                            strokeWidth={2}
                          />
                        ) : (
                          <ChevronDown
                            className={chevronClasses(isExpanded)}
                            strokeWidth={2}
                          />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </Link>

              {/* Sub-content */}
              {hasSubItems && (
                <div
                  className={[
                    "overflow-hidden transition-all duration-200 ease-in-out",
                    isExpanded
                      ? "max-h-[2000px] opacity-100"
                      : "max-h-0 opacity-0",
                  ].join(" ")}
                >
                  {isActiveSection && (
                    <div className="mx-3 border-t border-mainDivider" />
                  )}
                  <div
                    id={portalSlotId(section.path)}
                    className={
                      isActiveSection
                        ? "ml-3 border-l border-transparent pl-3 pr-3 pt-3 pb-4"
                        : "ml-3 mt-1 border-l border-mainDivider pl-3 pr-3 pb-2"
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );
};

const MainLeftSidebar = () => {
  const imgRef = useRef(null);

  return (
    <aside className="flex h-screen w-full flex-col bg-mainBg border-r border-mainDivider font-poppins font-normal text-mainText">
      <Link
        to="/home"
        type="button"
        className="flex items-center px-2 h-9 mt-3 flex-shrink-0 justify-center outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 rounded-lg cursor-pointer"
      >
        <img
          ref={imgRef}
          className="h-auto w-auto bg-transparent"
          src={entitylogo}
          alt=""
        />
      </Link>
      <div className="mx-3 mt-3 border-t border-mainDivider" />
      <div className="flex-1 overflow-y-auto w-full mt-2 min-h-0 pt-1">
        <TreeSection />
      </div>
    </aside>
  );
};

export default MainLeftSidebar;